import { Button } from "@/components/button"
import { BodyXs } from "@/domains/design/components/body-xs"
import { NfpmPendingNotice } from "@/domains/thick-v2/components/nfpm-pending-notice"
import { cn, dollarsPrettified } from "@/domains/library"

export type IncreasePositionViewProps = {
  token0Symbol: string
  token1Symbol: string
  /** Current pool mid price, pre-formatted as `X token1 per token0`. */
  displayMidPrice: string
  /** Position price range, pre-formatted as `low – high token1/token0`. */
  displayRange: string
  /** Whether the pool's current price sits within the position's range. */
  isInRange: boolean
  amount0: string
  amount1: string
  onChangeAmount0: (value: string) => void
  onChangeAmount1: (value: string) => void
  onFocusAmount0: () => void
  onFocusAmount1: () => void
  balance0Display: string
  balance1Display: string
  onUseBalance0: () => void
  onUseBalance1: () => void
  totalUsd: number
  slippagePercent: number
  canSubmit: boolean
  isSubmitting: boolean
  submissionError?: string
  onSubmit: () => void
  isDisabled?: boolean
  /** See `CollectFeesViewProps.nfpmTokenIdPending`. */
  nfpmTokenIdPending?: boolean
}

/**
 * Pure "add liquidity" (increase) view.
 *
 * Mirrors Uniswap v3 / Aerodrome Slipstream's add-liquidity surface: a pair
 * of token inputs (with the user's balance readily clickable), a reminder of
 * the position's locked price range, in-range indicator, a USD subtotal and
 * a slippage hint that matches the header settings gear. The view holds zero
 * state of its own — the parent composite drives every observable through
 * the `IncreasePositionStore`.
 */
export const IncreasePositionView = ({
  token0Symbol,
  token1Symbol,
  displayMidPrice,
  displayRange,
  isInRange,
  amount0,
  amount1,
  onChangeAmount0,
  onChangeAmount1,
  onFocusAmount0,
  onFocusAmount1,
  balance0Display,
  balance1Display,
  onUseBalance0,
  onUseBalance1,
  totalUsd,
  slippagePercent,
  canSubmit,
  isSubmitting,
  submissionError,
  onSubmit,
  isDisabled = false,
  nfpmTokenIdPending = false,
}: IncreasePositionViewProps) => {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-p400-light bg-p400 p-4 lg:p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-medium text-t100">Add liquidity</h2>
        <span className="text-xs text-t300">Slippage: {slippagePercent}%</span>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-md border border-p300-light bg-p300 px-3 py-2">
        <div className="flex min-w-0 flex-col">
          <BodyXs className="uppercase tracking-wide text-t300">Price range</BodyXs>
          <span className="truncate text-sm font-medium text-t100">{displayRange || "—"}</span>
          <span className="text-[11px] text-t300">Current price: {displayMidPrice || "—"}</span>
        </div>
        <span
          className={cn(
            "shrink-0 rounded px-2 py-0.5 text-[10px] font-medium",
            isInRange
              ? "bg-primary-400/20 text-primary-400"
              : "bg-error-background/15 text-error-text",
          )}
        >
          {isInRange ? "In range" : "Out of range"}
        </span>
      </div>

      <TokenAmountInput
        symbol={token0Symbol}
        amount={amount0}
        onChangeAmount={onChangeAmount0}
        onFocus={onFocusAmount0}
        balanceLabel={`Balance: ${balance0Display}`}
        onUseBalance={onUseBalance0}
        isDisabled={isDisabled}
      />
      <TokenAmountInput
        symbol={token1Symbol}
        amount={amount1}
        onChangeAmount={onChangeAmount1}
        onFocus={onFocusAmount1}
        balanceLabel={`Balance: ${balance1Display}`}
        onUseBalance={onUseBalance1}
        isDisabled={isDisabled}
      />

      <div className="flex items-center justify-between border-t border-p300-light pt-3 text-xs text-t300">
        <span>Deposit value</span>
        <span className="text-sm font-medium text-t100">{dollarsPrettified(totalUsd)}</span>
      </div>

      {nfpmTokenIdPending ? <NfpmPendingNotice actionLabel="Add liquidity" /> : null}

      {submissionError ? (
        <p className="rounded-md border border-error-border/40 bg-error-background/10 px-3 py-2 text-xs text-error-text">
          {submissionError}
        </p>
      ) : null}

      <Button
        variant={"primary"}
        size={"lg"}
        onClick={onSubmit}
        disabled={!canSubmit || isSubmitting || isDisabled}
        className={cn("w-full")}
      >
        {isSubmitting
          ? "Adding liquidity..."
          : nfpmTokenIdPending
            ? "Resolving NFP id…"
            : canSubmit
              ? "Add liquidity"
              : "Enter an amount"}
      </Button>
    </div>
  )
}

const TokenAmountInput = ({
  symbol,
  amount,
  onChangeAmount,
  onFocus,
  balanceLabel,
  onUseBalance,
  isDisabled,
}: {
  symbol: string
  amount: string
  onChangeAmount: (value: string) => void
  onFocus: () => void
  balanceLabel: string
  onUseBalance: () => void
  isDisabled: boolean
}) => (
  <div
    className={cn(
      "rounded-md border border-p300-light bg-p300 px-3 py-2.5 transition-colors focus-within:border-primary-400",
      isDisabled && "opacity-60",
    )}
  >
    <div className="mb-1.5 flex items-center justify-between">
      <BodyXs className="font-medium uppercase tracking-wide text-t300">{symbol}</BodyXs>
      <button
        type="button"
        onClick={onUseBalance}
        disabled={isDisabled}
        className={cn(
          "text-[11px] text-t300 transition-colors hover:text-t100",
          isDisabled ? "cursor-not-allowed" : "cursor-pointer",
        )}
      >
        {balanceLabel}
      </button>
    </div>
    <input
      type="text"
      inputMode="decimal"
      value={amount}
      onChange={(event) => onChangeAmount(event.currentTarget.value)}
      onFocus={onFocus}
      disabled={isDisabled}
      placeholder="0.0"
      className="w-full bg-transparent text-xl font-medium text-t100 outline-none placeholder:text-t300"
    />
  </div>
)
